import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Copy, ReactTable, t } from '@stokr/components-library'
import { Section } from 'components/sto/Section'
import { ExternalLinkIcon } from 'components/ui/icons'
import { Collapsible } from 'components/ui/collapsible'
import {
  useTrading,
  type MarketCell,
  type MarketTable,
  type OrderBookGroup,
  type TradingVenue,
} from 'hooks/sto/useTrading'
import { getAnchorIdFromHash } from 'hooks/sto/useStoAnchors'
import {
  Approx,
  Card,
  CardGrid,
  Caption,
  Change,
  ExchangeButton,
  GroupLabel,
  Logo,
  PairLine,
  PairLink as PairAnchor,
  Quote,
  QuoteBox,
  QuoteLabel,
  QuoteRow,
  QuoteValue,
  Root,
  Stack,
  TableFrame,
  VenueFooter,
  VenueInfo,
  VenueName,
} from './Trading.styles'
import { useStoTrack } from 'hooks/sto/useStoTracking'

// Headers resolve at render time: a module-level library `t()` can run before initCopy() and return raw keys.
const MARKET_COLUMNS = (
  [
    ['pair', 'trading.columns.pairHeader'],
    ['low', 'trading.columns.thirtyDayLow'],
    ['high', 'trading.columns.thirtyDayHigh'],
    ['last', 'trading.columns.last'],
    ['change', 'trading.columns.thirtyDayChange'],
    ['volume', 'trading.columns.thirtyDayVolume'],
    ['totalTokens', 'trading.columns.securitiesInCirculation'],
    ['marketCap', 'trading.columns.marketCap'],
  ] as const
).map(([accessor, id]) => ({ accessor, Header: () => t(id) }))

const external = { target: '_blank', rel: 'noopener noreferrer' } as const

const ExchangeLink = ({
  source,
  name,
  url,
}: {
  source: string
  /** Shown in full: "View on Bitfinex Securities". */
  name: string
  url: string
}) => {
  const trackSto = useStoTrack()
  return (
    <ExchangeButton
      as="a"
      href={url}
      {...external}
      black
      data-cy={`sto-trading-venue-${source}`}
      onClick={() => trackSto('sto_exchange_clicked', { venue: source, location: 'venue' })}>
      {t('stoPage.trading.viewOn', { exchange: name })}
      <ExternalLinkIcon width={12} height={12} aria-hidden="true" />
    </ExchangeButton>
  )
}

const PairLink = ({
  source,
  url,
  label,
  location = 'market_table',
}: {
  source: string
  url: string
  label: string
  location?: 'market_table' | 'order_book'
}) => {
  const trackSto = useStoTrack()
  return (
    <PairAnchor
      href={url}
      {...external}
      data-cy={location === 'order_book' ? `sto-trading-order-book-${source}` : `sto-trading-pair-${source}`}
      onClick={() => trackSto('sto_exchange_clicked', { venue: source, location, pair: label })}>
      {label}
      <ExternalLinkIcon width={12} height={12} aria-hidden="true" />
    </PairAnchor>
  )
}

const Cell = ({ cell }: { cell: MarketCell }) => (
  <>
    {cell.value}
    {cell.approx && <Approx>{cell.approx}</Approx>}
  </>
)

const Venues = ({ venues }: { venues: TradingVenue[] }) => (
  <CardGrid $gap={8}>
    {venues.map((venue) => (
      <Card key={venue.source} $gap={6}>
        {venue.logo && <Logo src={venue.logo} alt={venue.name} />}
        <VenueFooter>
          <VenueInfo>
            <VenueName>{venue.name}</VenueName>
            {venue.pairs.length > 0 && (
              <PairLine>
                <Copy id="trading.orderBook.tradingPairLabel" /> <strong>{venue.pairs.join(' - ')}</strong>
              </PairLine>
            )}
          </VenueInfo>
          <ExchangeLink source={venue.source} name={venue.name} url={venue.url} />
        </VenueFooter>
      </Card>
    ))}
  </CardGrid>
)

/** "SideSwap and Bitfinex Securities"; "A, B and C" for more. Only called with two or more. */
const listOf = (names: string[]) =>
  `${names.slice(0, -1).join(', ')} ${t('stoPage.trading.and')} ${names[names.length - 1]}`

const MarketTables = ({ tables }: { tables: MarketTable[] }) => (
  <Stack $gap={5}>
    {tables.length === 1 && (
      <Caption>
        <Copy id="stoPage.trading.marketCaption" values={{ exchange: tables[0].name }} />
        {tables[0].source === 'sideswap' && (
          <>
            <br />
            <Copy id="stoPage.trading.lbtcPrices" />
          </>
        )}
      </Caption>
    )}
    {tables.map((table) => (
      <Stack key={table.source} $gap={1}>
        {/* The logo names the exchange; the name only without one. */}
        {!table.logo && <GroupLabel>{table.name}</GroupLabel>}
        <Stack $gap={4}>
          {table.logo && <Logo src={table.logo} alt={table.name} />}
          <TableFrame>
            <ReactTable
              columns={MARKET_COLUMNS}
              data={table.rows.map((row) => ({
                pair: <PairLink source={table.source} url={row.pair.url} label={row.pair.label} />,
                low: <Cell cell={row.low} />,
                high: <Cell cell={row.high} />,
                last: <Cell cell={row.last} />,
                // Explicit null check: a 0% change is real data, not a missing value.
                change: row.change == null ? '-' : <Change $direction={row.change}>{row.change}%</Change>,
                volume: <Cell cell={row.volume} />,
                totalTokens: <Cell cell={row.totalTokens} />,
                marketCap: <Cell cell={row.marketCap} />,
              }))}
            />
          </TableFrame>
        </Stack>
      </Stack>
    ))}
  </Stack>
)

const OrderBooks = ({ groups }: { groups: OrderBookGroup[] }) => (
  <Stack $gap={8}>
    <Caption>
      {groups.length > 1 ? (
        <Copy id="stoPage.trading.orderBookCaptionMultiple" values={{ exchanges: listOf(groups.map((g) => g.name)) }} />
      ) : (
        <>
          <Copy id="stoPage.trading.orderBookCaption" values={{ exchange: groups[0].name }} />
          {groups[0].source === 'sideswap' && (
            <>
              {' '}
              <Copy id="stoPage.trading.lbtcPrices" />
            </>
          )}
        </>
      )}
    </Caption>
    {groups.map((group) => (
      <Stack key={group.source} $gap={1}>
        {!group.logo && <GroupLabel>{group.name}</GroupLabel>}
        <CardGrid $gap={5}>
          {group.cards.map((card) => (
            <Card key={card.key} $gap={4}>
              {group.logo && <Logo src={group.logo} alt={group.name} />}
              <PairLine>
                <Copy id="trading.orderBook.tradingPairLabel" />{' '}
                {card.url ? (
                  <PairLink source={group.source} url={card.url} label={card.tradingPair} location="order_book" />
                ) : (
                  <strong>{card.tradingPair}</strong>
                )}
              </PairLine>
              <QuoteRow>
                <QuoteBox>
                  <Quote $align="start">
                    <QuoteLabel>{t('trading.columns.amount')}</QuoteLabel>
                    <QuoteValue>{card.bidAmount}</QuoteValue>
                  </Quote>
                  <Quote $align="end">
                    <QuoteLabel>{t('trading.columns.bid')}</QuoteLabel>
                    <QuoteValue $tone="bid">{card.bidPrice}</QuoteValue>
                  </Quote>
                </QuoteBox>
                <QuoteBox>
                  <Quote $align="start">
                    <QuoteLabel>{t('trading.columns.offer')}</QuoteLabel>
                    <QuoteValue $tone="ask">{card.askPrice}</QuoteValue>
                  </Quote>
                  <Quote $align="end">
                    <QuoteLabel>{t('trading.columns.amount')}</QuoteLabel>
                    <QuoteValue>{card.askAmount}</QuoteValue>
                  </Quote>
                </QuoteBox>
              </QuoteRow>
            </Card>
          ))}
        </CardGrid>
      </Stack>
    ))}
  </Stack>
)

type TradingPanelId = 'trading-secondary-market' | 'trading-order-book'

/** Closed by default at every width; a deep link opens its panel. */
const usePanelsOpen = () => {
  const { hash } = useLocation()
  const [opened, setOpened] = useState<Partial<Record<TradingPanelId, boolean>>>({})

  useEffect(() => {
    const id = getAnchorIdFromHash(hash)
    if (id === 'trading-secondary-market' || id === 'trading-order-book') setOpened((prev) => ({ ...prev, [id]: true }))
  }, [hash])

  return {
    isOpen: (id: TradingPanelId) => opened[id] ?? false,
    onToggle: (id: TradingPanelId) => (open: boolean) => setOpened((prev) => ({ ...prev, [id]: open })),
  }
}

export const Trading = () => {
  const state = useTrading()
  const panels = usePanelsOpen()
  const trackSto = useStoTrack()
  const onToggle = (id: TradingPanelId) => (open: boolean) => {
    trackSto('sto_trading_panel_toggled', { panel: id.replace('trading-', ''), open })
    panels.onToggle(id)(open)
  }

  return (
    <Section anchor="trading" title={<Copy id="stoPage.trading.title" />} state={state}>
      {({ venues, marketTables, orderBooks }) => (
        <Root>
          <Stack $gap={8}>
            {venues.length > 0 && <Venues venues={venues} />}

            {(marketTables.length > 0 || orderBooks.length > 0) && (
              <Stack $gap={4}>
                {marketTables.length > 0 && (
                  <Collapsible
                    id="trading-secondary-market"
                    title={t('trading.secondaryMarket.title')}
                    open={panels.isOpen('trading-secondary-market')}
                    onToggle={onToggle('trading-secondary-market')}
                    dataCy="sto-trading-secondary-market-toggle"
                    divided>
                    <MarketTables tables={marketTables} />
                  </Collapsible>
                )}
                {orderBooks.length > 0 && (
                  <Collapsible
                    id="trading-order-book"
                    title={t('trading.orderBook.title')}
                    open={panels.isOpen('trading-order-book')}
                    onToggle={onToggle('trading-order-book')}
                    dataCy="sto-trading-order-book-toggle"
                    divided>
                    <OrderBooks groups={orderBooks} />
                  </Collapsible>
                )}
              </Stack>
            )}
          </Stack>
        </Root>
      )}
    </Section>
  )
}
